import { useEffect } from 'react';

/**
 * Iter 67/68: Frontend noindex enforcer + admin title setter.
 *
 * The public site's index.html ships a hardcoded
 *   <meta name="robots" content="index, follow, ...">
 * The Helmet library deduplicates by attribute on its OWN tags but does
 * not touch raw <meta> tags that were placed in index.html.  As a result,
 * when this component is mounted on /admin/* or /portal/*, simply
 * appending a SECOND <meta name="robots"> tag still leaves Google reading
 * the original "index, follow" — and Sandy's admin pages were getting
 * indexed.
 *
 * Iter 68 fix: this component now MUTATES the existing meta robots tag
 * (and the `googlebot` / `bingbot` siblings if present) to noindex while
 * mounted, then RESTORES the original values on unmount so the public
 * site's indexing instructions are unaffected.  Belt-and-braces: also
 * appends an extra noindex tag tagged with `data-shs-noindex` for
 * search engines that read the first matching robots tag they encounter.
 *
 * It also overrides document.title (which the public site sets via Helmet
 * to "Sustainable Handyman Services | Abergavenny ...") so the admin
 * tab/PWA shows a sensible title like "Admin | Sustainable Handyman
 * Services".  Default is "Admin | Sustainable Handyman Services" — pass a
 * custom `title` prop to override on a per-page basis.
 */
const ROBOTS_NAMES = ['robots', 'googlebot', 'bingbot'];
const NOINDEX_VALUE = 'noindex, nofollow, noarchive, max-snippet:0, max-image-preview:none';
const DEFAULT_TITLE = 'Admin | Sustainable Handyman Services';

export default function NoindexHead({ title = DEFAULT_TITLE } = {}) {
  useEffect(() => {
    const previous = [];

    // 1. Override every existing robots-style meta tag.
    ROBOTS_NAMES.forEach((name) => {
      const tag = document.querySelector(`meta[name="${name}"]`);
      if (tag) {
        previous.push({ tag, prev: tag.getAttribute('content') });
        tag.setAttribute('content', NOINDEX_VALUE);
      }
    });

    // 2. If for some reason no meta robots tag exists, inject one so the
    // signal is present regardless of the upstream HTML template.
    if (!document.querySelector('meta[name="robots"]')) {
      const inj = document.createElement('meta');
      inj.name = 'robots';
      inj.content = NOINDEX_VALUE;
      inj.setAttribute('data-shs-noindex', 'injected');
      document.head.appendChild(inj);
      previous.push({ tag: inj, prev: null });
    }

    // 3. Override document.title.  Helmet on the public site renders
    // titles asynchronously, which can race with admin route changes —
    // setting it imperatively here guarantees the correct title is
    // visible in the browser tab and PWA card.
    const previousTitle = document.title;
    if (title) document.title = title;

    return () => {
      // Restore original values when the user navigates away from a
      // protected page so the public site re-enables indexing.
      previous.forEach(({ tag, prev }) => {
        if (prev === null) {
          tag.parentNode && tag.parentNode.removeChild(tag);
        } else {
          tag.setAttribute('content', prev);
        }
      });
      document.title = previousTitle;
    };
  }, [title]);
  return null;
}
